// The job's working dates, as the screens show them: when the work runs, and one quiet
// marker when it runs outside the retainer it sits under.
//
// SHARED BY THREE PLACES — the job screen, the retainer's topics table, and the client
// card's nested job rows — because the whole point of the dates is to be visible without
// opening the job. Three renderings of a date range is three chances for them to disagree
// about what an open-ended one looks like.
//
// THIS IS THE QUIET HALF OF components/RetainerTerm.js, and the difference is deliberate.
// A retainer's term counts down, badges itself amber and red, and asks to be renewed —
// because a retainer bills monthly and can genuinely be behind. A job bills nothing on a
// schedule, so there is no "near end" here, no countdown and no prompt: a job repeating
// its retainer's warning would be one engagement nagging twice.
//
// `beyondRetainer` is computed on the SERVER (calculations/jobTerm) and arrives on
// `term`, like every figure in RetainerTerm — a browser deciding for itself where a
// contract ends is exactly what the thin-client rule prevents.
import he from '../i18n/he';
import DateText, { formatDate } from './DateText';

// The range itself. Each shape reads as a fact rather than as a half-filled form: a job
// with only a start says "from the 1st", not "01/02/2026 – —", which looks like something
// failed to load.
export const JobTermText = ({ term, fallback }) => {
  const { startDate, endDate } = term || {};
  if (!startDate && !endDate) {
    return <span className="muted">{fallback ?? he.common.empty}</span>;
  }
  if (!endDate) {
    return <>{he.jobs.workTermFromPrefix}<DateText value={startDate} /></>;
  }
  if (!startDate) {
    return <>{he.jobs.workTermUntilPrefix} <DateText value={endDate} /></>;
  }
  return <><DateText value={startDate} />{' – '}<DateText value={endDate} /></>;
};

// The marker, and everything it is not: it does not colour the row, filter it, sort it or
// stop anything being recorded on it. Work running past its engagement is ordinary, so
// this is addressed to a reader and to nobody else — the title says as much, because a
// badge with no explanation invites someone to treat it as an error.
//
// IT NAMES WHICH DIRECTION, and that is what was wrong with it before. One label reading
// "beyond the retainer's term" covered both bounds, and "beyond" is read as "past the end"
// by anyone who has not just read the code — so a job that STARTED four days before its
// engagement was reported in words that sent its reader looking at the end date. The
// server now says which way it escaped ('before' / 'after' / 'both') and the badge says
// it too. Direction, never field: a job starting after the engagement ended escapes
// through its start date, and a label read off the field would call that "before".
//
// It also shows the term it was measured against. A badge that asserts a job is outside
// its engagement, while the engagement's own dates are on a different screen, is a claim
// the reader has to go elsewhere to check — and checking it is how the wrong conclusion
// gets drawn.
const DIRECTION_LABEL = {
  before: () => he.jobs.beforeRetainerTerm,
  after: () => he.jobs.afterRetainerTerm,
  both: () => he.jobs.beyondRetainerTerm,
};

export const BeyondRetainerBadge = ({ term }) => {
  if (!term || !term.beyondRetainer) return null;
  const label = DIRECTION_LABEL[term.beyondRetainer];
  // An unknown value renders nothing rather than an empty badge: a marker that cannot
  // say what it means is worse than no marker.
  if (!label) return null;
  return (
    <span
      className="badge amber"
      title={he.jobs.beyondRetainerTermHint(
        formatDate(term.retainerStartDate, he.jobs.workTermOpen),
        formatDate(term.retainerEndDate, he.jobs.workTermOpen),
      )}
    >
      {label()}
    </span>
  );
};

// One table cell or one row of badges: the dates, and the marker when there is one.
export const JobTermCell = ({ term }) => (
  <>
    <JobTermText term={term} />
    {' '}
    <BeyondRetainerBadge term={term} />
  </>
);

// The job screen's line, beside the client and the agreed value. Named, unlike the cell —
// a bare date range in a column of labelled facts would leave the reader to guess which
// dates these are, and a job has several others (an expense's, a publication's).
export const JobTermLine = ({ term }) => (
  <p className="muted">
    {he.jobs.workTerm}:{' '}
    <strong><JobTermText term={term} fallback={he.jobs.workTermNone} /></strong>
    {' '}<BeyondRetainerBadge term={term} />
  </p>
);

export default JobTermLine;
